import React, { useState, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import Swal from 'sweetalert2'
import {
  ArrowRight,
  ArrowUpRight,
  CheckCircle2,
  Clock3,
  Globe2,
  Headphones,
  PackageCheck,
  RefreshCw,
  Send,
  ShieldCheck,
  Truck,
  Zap
} from 'lucide-react'

const API_URL = process.env.REACT_APP_BACKEND_URL

const benefits = [
  {
    icon: Globe2,
    number: '01',
    title: 'Nationwide Reach',
    description:
      'Connect your business to a reliable logistics network across India, reaching customers across cities and regions with efficient transportation and dependable delivery services.'
  },
  {
    icon: ShieldCheck,
    number: '02',
    title: 'Transparent Pricing',
    description:
      'Plan your logistics budget with confidence through clear pricing, transparent service costs, and reliable solutions designed to support your business growth and operational efficiency.'
  },
  {
    icon: Headphones,
    number: '03',
    title: 'Dedicated Support',
    description:
      'Get personalized assistance from a responsive team that understands your shipping needs, helps resolve queries, and works to ensure smooth day-to-day logistics operations.'
  },
  {
    icon: Zap,
    number: '04',
    title: 'Smart Technology',
    description:
      'Simplify shipment management with convenient digital solutions that improve visibility, streamline processes, support better coordination, and help you manage deliveries more efficiently.'
  },
  {
    icon: PackageCheck,
    number: '05',
    title: 'Flexible Solutions',
    description:
      'Choose shipping and logistics options tailored to your products, delivery requirements, order volumes, and business goals, with the flexibility to adapt as your operations grow.'
  },
  {
    icon: Clock3,
    number: '06',
    title: 'Reliable Timelines',
    description:
      'Keep your business moving with dependable pickup and delivery coordination, efficient shipment handling, and a focus on timely service to support customer satisfaction.'
  }
]

const fields = [
  {
    id: 'name',
    label: 'Full Name',
    placeholder: 'Enter your full name',
    type: 'text',
    autoComplete: 'name'
  },
  {
    id: 'company_name',
    label: 'Company Name',
    placeholder: 'Enter your company name',
    type: 'text',
    autoComplete: 'organization'
  },
  {
    id: 'contact',
    label: 'Contact Number',
    placeholder: 'Enter your contact number',
    type: 'tel',
    autoComplete: 'tel'
  },
  {
    id: 'email',
    label: 'Business Email',
    placeholder: 'you@company.com',
    type: 'email',
    autoComplete: 'email'
  },
  {
    id: 'address',
    label: 'Business Address',
    placeholder: 'Enter your address',
    type: 'text',
    autoComplete: 'street-address'
  },
  {
    id: 'city',
    label: 'City',
    placeholder: 'Enter your city',
    type: 'text',
    autoComplete: 'address-level2'
  },
  {
    id: 'state',
    label: 'State',
    placeholder: 'Enter your state',
    type: 'text',
    autoComplete: 'address-level1'
  },
  {
    id: 'pincode',
    label: 'PIN Code',
    placeholder: 'Enter your PIN code',
    type: 'text',
    autoComplete: 'postal-code'
  }
]

const ClientRegistration = () => {
  const [formData, setFormData] = useState({
    name: '',
    company_name: '',
    address: '',
    city: '',
    state: '',
    contact: '',
    email: '',
    pincode: '',
    captchaInput: ''
  })

  const [captcha, setCaptcha] = useState({
    question: '',
    answer: 0
  })

  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    generateCaptcha()
  }, [])

  const generateCaptcha = () => {
    const a = Math.floor(Math.random() * 10) + 1
    const b = Math.floor(Math.random() * 10) + 1

    setCaptcha({
      question: `${a} + ${b}`,
      answer: a + b
    })
  }

  const handleChange = e => {
    const { name, value } = e.target

    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const resetForm = () => {
    setFormData({
      name: '',
      company_name: '',
      address: '',
      city: '',
      state: '',
      contact: '',
      email: '',
      pincode: '',
      captchaInput: ''
    })

    generateCaptcha()
  }

  const handleSubmit = async e => {
    e.preventDefault()

    if (Number(formData.captchaInput) !== captcha.answer) {
      await Swal.fire({
        icon: 'error',
        title: 'Incorrect Answer',
        text: 'Please solve the verification question and try again.',
        confirmButtonColor: '#11194C'
      })

      setFormData(prev => ({
        ...prev,
        captchaInput: ''
      }))

      generateCaptcha()
      return
    }

    if (!API_URL) {
      await Swal.fire({
        icon: 'error',
        title: 'Configuration Error',
        text: 'The registration service is currently unavailable.',
        confirmButtonColor: '#11194C'
      })
      return
    }

    setSubmitting(true)

    try {
      const response = await fetch(
        `${API_URL.replace(/\/+$/, '')}/clients/register`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(formData)
        }
      )

      const result = await response.json()

      if (!response.ok) {
        throw new Error(result?.error || 'Unable to complete registration.')
      }

      await Swal.fire({
        icon: 'success',
        title: 'Welcome Aboard!',
        text: 'Your client registration was submitted successfully.',
        confirmButtonColor: '#11194C'
      })

      resetForm()
    } catch (err) {
      await Swal.fire({
        icon: 'error',
        title: 'Registration Failed',
        text: err.message || 'Something went wrong. Please try again.',
        confirmButtonColor: '#11194C'
      })

      generateCaptcha()
      setFormData(prev => ({
        ...prev,
        captchaInput: ''
      }))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      <Helmet>
        <title>Client Registration | S Infinite Express</title>
        <meta
          name='description'
          content='Register your business with S Infinite Express for reliable shipping, flexible logistics solutions, and dedicated business support.'
        />
        <meta
          name='keywords'
          content='client registration, business shipping India, logistics partnership, S Infinite Express, freight solutions'
        />
        <meta
          property='og:title'
          content='Client Registration | S Infinite Express'
        />
        <meta
          property='og:description'
          content='Build a stronger logistics partnership with S Infinite Express.'
        />
        <meta property='og:type' content='website' />
        <meta
          property='og:url'
          content='https://sinfiniteexpress.com/join-us/client-registration'
        />
        <link
          rel='canonical'
          href='https://sinfiniteexpress.com/join-us/client-registration'
        />
      </Helmet>

      <main className='overflow-hidden bg-white font-sans text-[#17213D]'>
        {/* HERO */}
        <section className='relative isolate overflow-hidden bg-[#10183F]'>
          <div className='absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(255,244,79,0.15),_transparent_42%)]' />

          <div className='absolute -right-24 top-10 h-80 w-80 rounded-full border border-white/10 sm:h-[28rem] sm:w-[28rem]' />
          <div className='absolute -right-12 top-24 h-64 w-64 rounded-full border border-white/10 sm:h-96 sm:w-96' />

          <div className='relative mx-auto grid max-w-[1440px] items-center gap-12 px-5 py-16 sm:px-8 sm:py-20 lg:grid-cols-[1.1fr_0.9fr] lg:gap-16 lg:px-10 lg:py-14'>
            <div className='max-w-2xl'>
              <h1 className='text-4xl font-black leading-[1.12] tracking-tight text-white sm:text-5xl lg:text-6xl xl:text-7xl'>
                Let's Move Your
                <span className='mt-1 block text-[#FFF44F]'>
                  Business Forward.
                </span>
              </h1>

              <p className='mt-6 max-w-xl text-sm leading-7 text-white/70 sm:text-base sm:leading-8'>
                Join S Infinite Express and discover dependable shipping,
                smarter logistics, and a partnership designed around your
                business growth.
              </p>

              <div className='mt-8 flex flex-wrap items-center gap-4'>
                <a
                  href='#registration-form'
                  className='group inline-flex items-center gap-3 rounded-xl bg-[#FFF44F] px-6 py-4 text-sm font-extrabold text-[#11194C] shadow-lg shadow-yellow-400/10 transition duration-300 hover:-translate-y-1 hover:bg-white'
                >
                  Register Your Business
                  <ArrowRight
                    size={18}
                    className='transition-transform group-hover:translate-x-1'
                  />
                </a>

                <div className='flex items-center gap-3 text-sm text-white/75'>
                  <ShieldCheck size={20} className='text-[#FFF44F]' />
                  Business-focused solutions
                </div>
              </div>

              <div className='mt-12 grid max-w-lg grid-cols-3 gap-4 border-t border-white/15 pt-6'>
                {[
                  { value: '01', label: 'Easy Registration' },
                  { value: '02', label: 'Flexible Shipping' },
                  { value: '03', label: 'Dedicated Support' }
                ].map(item => (
                  <div key={item.value}>
                    <span className='text-xs font-black text-[#FFF44F]'>
                      {item.value}
                    </span>
                    <p className='mt-2 text-xs leading-5 text-white/70 sm:text-sm'>
                      {item.label}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* HERO VISUAL */}
            <div className='relative mx-auto w-full max-w-lg lg:ml-auto'>
              <div className='absolute -inset-4 rounded-[2rem] bg-[#FFF44F]/10 blur-2xl' />

              <div className='relative overflow-hidden rounded-[2rem] border border-white/15 bg-gradient-to-br from-white/[0.12] to-white/[0.03] p-5 shadow-2xl backdrop-blur-sm sm:p-6'>
                <div className='flex items-center justify-between gap-4'>
                  <div>
                    <h2 className='mt-1 text-2xl font-extrabold text-white sm:text-3xl'>
                      Built Around
                      <span className='block text-[#FFF44F]'>
                        Your Business
                      </span>
                    </h2>
                  </div>
                </div>

                <div className='relative my-5 overflow-hidden rounded-2xl border border-white/10 bg-[#0B1231]/60 p-5 sm:p-7'>
                  <div className='absolute right-0 top-0 h-32 w-32 rounded-full bg-[#FFF44F]/10 blur-3xl' />

                  <div className='relative flex items-center justify-between gap-3'>
                    <div className='flex items-center gap-3'>
                      <div className='flex h-11 w-11 items-center justify-center rounded-xl bg-white/10 text-[#FFF44F]'>
                        <PackageCheck size={22} />
                      </div>
                      <div>
                        <p className='text-sm font-bold text-white'>
                          Shipping Solutions
                        </p>
                        <p className='mt-1 text-xs text-white/50'>
                          Tailored to your needs
                        </p>
                      </div>
                    </div>
                    <CheckCircle2
                      size={21}
                      className='shrink-0 text-[#FFF44F]'
                    />
                  </div>

                  <div className='my-6 h-px bg-white/10' />

                  <div className='flex items-center justify-between gap-3'>
                    <div className='flex items-center gap-3'>
                      <div className='flex h-11 w-11 items-center justify-center rounded-xl bg-white/10 text-[#FFF44F]'>
                        <Globe2 size={22} />
                      </div>
                      <div>
                        <p className='text-sm font-bold text-white'>
                          Connected Network
                        </p>
                        <p className='mt-1 text-xs text-white/50'>
                          Helping businesses move forward
                        </p>
                      </div>
                    </div>
                    <ArrowUpRight
                      size={21}
                      className='shrink-0 text-white/60'
                    />
                  </div>
                </div>

                <div className='flex items-center gap-3'>
                  <div className='flex -space-x-2'>
                    {[0, 1, 2].map(item => (
                      <div
                        key={item}
                        className='flex h-9 w-9 items-center justify-center rounded-full border-2 border-[#172044] bg-[#FFF44F] text-[#11194C]'
                      >
                        {item === 0 && <Truck size={15} />}
                        {item === 1 && <PackageCheck size={15} />}
                        {item === 2 && <ShieldCheck size={15} />}
                      </div>
                    ))}
                  </div>
                  <p className='text-xs leading-5 text-white/65'>
                    One partner for your evolving logistics needs.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className='h-1.5 bg-gradient-to-r from-[#FFF44F] via-[#FFF99B] to-transparent' />
        </section>

        {/* BENEFITS */}
        <section className='relative px-5 py-12 sm:px-8 sm:py-20 lg:px-14 lg:py-8'>
          <div className='mx-auto max-w-[1440px]'>
            <div className='mx-auto max-w-2xl text-center'>
              <h2 className='mt-1 text-2xl sm:text-3xl lg:text-4xl font-black leading-tight tracking-tight text-[#11194C] whitespace-nowrap'>
                More Than Shipping.{' '}
                <span className='text-[#d32c30]'>A Better Partnership.</span>
              </h2>

              <p className='mx-auto mt-2 max-w-none whitespace-nowrap text-xs leading-7 text-slate-600 sm:text-sm lg:text-base'>
                We combine reliable service, practical solutions, and dedicated
                support to help your business keep moving.
              </p>
            </div>

            <div className='mt-1 grid gap-5 sm:grid-cols-2 lg:mt-16 lg:grid-cols-3'>
              {benefits.map(benefit => {
                const Icon = benefit.icon

                return (
                  <article
                    key={benefit.title}
                    className='group relative overflow-hidden rounded-2xl border border-slate-200/80 bg-white p-6 transition duration-300 hover:-translate-y-1.5 hover:border-[#E8D94B] hover:shadow-[0_20px_50px_rgba(17,25,76,0.09)] sm:p-7'
                  >
                    <div className='absolute right-5 top-4 text-4xl font-black text-slate-100 transition-colors group-hover:text-[#FFF5A8]'>
                      {benefit.number}
                    </div>

                    <div className='relative flex h-12 w-12 items-center justify-center rounded-xl bg-[#11194C] text-[#FFF44F] transition-colors duration-300 group-hover:bg-[#FFF44F] group-hover:text-[#11194C]'>
                      <Icon size={23} strokeWidth={1.8} />
                    </div>

                    <h3 className='mt-6 text-lg font-extrabold text-[#11194C]'>
                      {benefit.title}
                    </h3>

                    <p className='mt-3 text-sm leading-7 text-slate-600'>
                      {benefit.description}
                    </p>
                  </article>
                )
              })}
            </div>
          </div>
        </section>

        {/* REGISTRATION FORM */}
        <section
          id='registration-form'
          className='relative isolate scroll-mt-1 overflow-hidden bg-[#F4F6FC] px-4 py-12 sm:px-8 sm:py-16 lg:px-12 lg:py-10'
        >
          {/* Background */}
          <div className='pointer-events-none absolute inset-0 -z-10 overflow-hidden'>
            <div className='absolute -left-32 -top-24 h-96 w-96 rounded-full bg-[#FFF44F]/25 blur-[110px]' />
            <div className='absolute -bottom-32 -right-20 h-[28rem] w-[28rem] rounded-full bg-blue-200/50 blur-[120px]' />
            <div className='absolute inset-0 opacity-[0.035] [background-image:radial-gradient(#11194C_1px,transparent_1px)] [background-size:24px_24px]' />
          </div>

          <div className='relative mx-auto grid max-w-[1240px] items-center gap-10 lg:grid-cols-[0.85fr_1.15fr] lg:gap-14'>
            {/* LEFT — INTRO */}
            <div className='relative lg:py-5'>
              <div className='inline-flex items-center gap-2 rounded-full border border-[#11194C]/10 bg-white/80 px-3.5 py-2 shadow-sm backdrop-blur'>
                <span className='h-2 w-2 rounded-full bg-emerald-500' />
                <span className='text-[10px] font-extrabold uppercase tracking-[0.18em] text-[#11194C] sm:text-xs'>
                  Partner With Us
                </span>
              </div>

              <h2 className='mt-5 whitespace-nowrap text-2xl font-black leading-tight tracking-tight text-[#11194C] sm:text-3xl lg:text-[2.7rem]'>
                Move Your Business{' '}
                <span className='text-[#d32c30]'>Forward.</span>
              </h2>

              <p className='mt-5 max-w-lg text-sm leading-7 text-slate-600 sm:text-base'>
                Join S Infinite Express for dependable logistics, streamlined
                shipping, and flexible delivery solutions built around your
                business needs.
              </p>

              {/* Benefits */}
              <div className='mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-1'>
                {[
                  {
                    icon: CheckCircle2,
                    title: 'Quick Registration',
                    desc: 'Get started with a simple registration process.'
                  },
                  {
                    icon: PackageCheck,
                    title: 'Flexible Logistics',
                    desc: 'Solutions tailored to your business requirements.'
                  },
                  {
                    icon: ShieldCheck,
                    title: 'Reliable Support',
                    desc: 'A dedicated team ready to assist your journey.'
                  }
                ].map((item, index) => {
                  const Icon = item.icon

                  return (
                    <div
                      key={item.title}
                      className='group relative flex items-center gap-4 overflow-hidden rounded-2xl border border-white/90 bg-white/80 p-4 shadow-[0_5px_20px_rgba(17,25,76,0.04)] backdrop-blur-sm transition-all duration-300 hover:-translate-y-1 hover:border-[#11194C]/10 hover:bg-white hover:shadow-[0_14px_35px_rgba(17,25,76,0.09)]'
                    >
                      <div className='flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#11194C] text-[#FFF44F] shadow-md shadow-[#11194C]/15 transition-transform duration-300 group-hover:scale-105'>
                        <Icon size={21} />
                      </div>

                      <div className='min-w-0 flex-1'>
                        <p className='text-sm font-extrabold text-[#11194C]'>
                          {item.title}
                        </p>
                        <p className='mt-1 text-xs leading-5 text-slate-500'>
                          {item.desc}
                        </p>
                      </div>

                      <span className='self-start text-[10px] font-black tracking-widest text-slate-300'>
                        0{index + 1}
                      </span>
                    </div>
                  )
                })}
              </div>

              {/* Support Card */}
              <div className='relative mt-6 overflow-hidden rounded-2xl bg-[#11194C] p-5 shadow-xl shadow-[#11194C]/15'>
                <div className='absolute -right-8 -top-12 h-32 w-32 rounded-full border border-white/10' />
                <div className='absolute -right-1 top-8 h-16 w-16 rounded-full border border-white/10' />

                <div className='relative flex items-center gap-3.5'>
                  <div className='flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#FFF44F] text-[#11194C]'>
                    <Headphones size={23} />
                  </div>

                  <div className='min-w-0 flex-1'>
                    <p className='text-sm font-extrabold text-white'>
                      Need assistance?
                    </p>
                    <p className='mt-1 text-xs leading-5 text-white/65'>
                      Register your interest and let our team connect with you.
                    </p>
                  </div>

                  <ArrowRight size={20} className='shrink-0 text-[#FFF44F]' />
                </div>
              </div>
            </div>

            {/* RIGHT — FORM */}
            <div className='relative'>
              <div className='absolute -inset-3 -z-10 rounded-[2rem] bg-gradient-to-br from-[#FFF44F]/50 via-white/80 to-blue-200/70 blur-2xl' />

              <div className='overflow-hidden rounded-2xl border border-white/80 bg-white shadow-[0_25px_80px_rgba(17,25,76,0.13)] sm:rounded-3xl'>
                {/* Form Header */}
                <div className='relative overflow-hidden bg-[#11194C] px-5 py-5 sm:px-7 sm:py-6'>
                  <div className='absolute -right-10 -top-20 h-48 w-48 rounded-full border border-white/[0.08]' />
                  <div className='absolute right-7 top-7 h-24 w-24 rounded-full border border-white/[0.08]' />

                  <div className='relative flex items-center gap-3.5'>
                    <div>
                      <p className='text-[10px] font-bold uppercase tracking-[0.2em] text-[#FFF44F]'>
                        Let's Get Started
                      </p>
                      <p className='mt-1 text-xs text-white/60'>
                        Complete the details below to register your business.
                      </p>
                    </div>
                  </div>
                </div>

                <form onSubmit={handleSubmit} className='p-4 sm:p-6 lg:p-7'>
                  {/* Form Fields */}
                  <div className='grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2 lg:grid-cols-6'>
                    {fields.map(field => {
                      const Icon = field.icon

                      const fieldLayout = {
                        name: 'sm:col-span-1 lg:col-span-3',
                        company_name: 'sm:col-span-1 lg:col-span-3',
                        contact: 'sm:col-span-1 lg:col-span-3',
                        email: 'sm:col-span-1 lg:col-span-3',
                        address: 'sm:col-span-2 lg:col-span-6',
                        city: 'sm:col-span-1 lg:col-span-2',
                        state: 'sm:col-span-1 lg:col-span-2',
                        pincode: 'sm:col-span-1 lg:col-span-2'
                      }

                      return (
                        <div
                          key={field.id}
                          className={fieldLayout[field.id] || ''}
                        >
                          <label
                            htmlFor={field.id}
                            className='mb-1.5 block text-xs font-bold text-slate-700'
                          >
                            {field.label}
                            <span className='ml-1 text-red-500'>*</span>
                          </label>

                          <div className='relative'>
                            {Icon && (
                              <Icon
                                size={15}
                                className='pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400'
                              />
                            )}

                            <input
                              id={field.id}
                              name={field.id}
                              type={field.type}
                              value={formData[field.id]}
                              onChange={handleChange}
                              placeholder={field.placeholder}
                              autoComplete={field.autoComplete}
                              required
                              className={`w-full rounded-xl border border-slate-200 bg-slate-50/70 py-3 pr-3 text-sm text-[#17213D] outline-none transition-all duration-200 placeholder:text-slate-400 hover:border-slate-300 focus:border-[#11194C] focus:bg-white focus:ring-4 focus:ring-[#11194C]/[0.06] ${
                                Icon ? 'pl-9' : 'pl-3'
                              }`}
                            />
                          </div>
                        </div>
                      )
                    })}
                  </div>

                  {/* CAPTCHA + Submit */}
                  <div className='mt-6 grid grid-cols-1 items-stretch gap-3 sm:grid-cols-2'>
                    <div className='flex min-w-0 items-center gap-2 rounded-xl border border-[#11194C]/10 bg-slate-50 p-3'>
                      <div className='flex shrink-0 items-center gap-1.5 rounded-lg bg-white px-2 py-2 shadow-sm'>
                        <ShieldCheck size={15} className='text-[#11194C]' />
                        <span className='text-xs font-extrabold text-[#11194C]'>
                          {captcha.question} = ?
                        </span>
                        <button
                          type='button'
                          onClick={generateCaptcha}
                          aria-label='Refresh CAPTCHA'
                          className='rounded-md p-1 text-slate-500 transition hover:bg-slate-100 hover:text-[#11194C]'
                        >
                          <RefreshCw size={13} />
                        </button>
                      </div>

                      <input
                        id='captchaInput'
                        name='captchaInput'
                        type='number'
                        value={formData.captchaInput}
                        onChange={handleChange}
                        placeholder='Answer'
                        required
                        className='min-w-0 w-full flex-1 rounded-lg border border-slate-200 bg-white px-2.5 py-2.5 text-sm outline-none transition placeholder:text-slate-400 focus:border-[#11194C] focus:ring-2 focus:ring-[#11194C]/10'
                      />
                    </div>

                    <button
                      type='submit'
                      disabled={submitting}
                      className='group flex w-full items-center justify-center gap-2 rounded-xl bg-[#11194C] px-4 py-3.5 text-sm font-extrabold text-white shadow-lg shadow-[#11194C]/15 transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#1A2868] hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-60'
                    >
                      {submitting ? (
                        <>
                          <RefreshCw size={17} className='animate-spin' />
                          Submitting...
                        </>
                      ) : (
                        <>
                          <Send size={16} />
                          Submit Registration
                          <ArrowRight
                            size={17}
                            className='transition-transform duration-300 group-hover:translate-x-1'
                          />
                        </>
                      )}
                    </button>
                  </div>

                  <div className='mt-4 flex items-center justify-center gap-2 text-center'>
                    <ShieldCheck
                      size={13}
                      className='shrink-0 text-emerald-600'
                    />
                    <p className='text-[11px] leading-5 text-slate-500'>
                      Your information is used to process your business
                      registration.
                    </p>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </section>

        {/* BOTTOM CTA */}
        <section className='bg-white px-5 py-12 sm:px-8 lg:px-14 lg:py-16'>
          <div className='relative mx-auto flex max-w-[1280px] flex-col gap-6 overflow-hidden rounded-3xl bg-[#11194C] px-6 py-9 sm:px-10 sm:py-10 lg:flex-row lg:items-center lg:justify-between lg:px-12'>
            <div className='absolute -right-10 -top-24 h-56 w-56 rounded-full bg-[#FFF44F]/10 blur-2xl' />

            <div className='relative'>
              <p className='text-xs font-bold uppercase tracking-[0.2em] text-[#FFF44F]'>
                Ready to move forward?
              </p>
              <h2 className='mt-3 text-2xl font-black text-white sm:text-3xl'>
                Let's Build a Better Logistics Journey.
              </h2>
              <p className='mt-3 max-w-2xl text-sm leading-6 text-white/65'>
                Start your business registration today and explore logistics
                solutions designed for your needs.
              </p>
            </div>

            <a
              href='#registration-form'
              className='group relative inline-flex shrink-0 items-center justify-center gap-3 self-start rounded-xl bg-[#FFF44F] px-6 py-4 text-sm font-extrabold text-[#11194C] transition hover:-translate-y-1 hover:bg-white lg:self-center'
            >
              Get Started
              <ArrowUpRight
                size={18}
                className='transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5'
              />
            </a>
          </div>
        </section>
      </main>
    </>
  )
}

export default ClientRegistration
